/* eslint-disable @typescript-eslint/member-ordering */
import { observable, action, computed, reaction } from 'mobx';
import ErrorStore from '../../common/stores/ErrorStore';
import _ from 'lodash';
import { ProjectAssignment } from '../models/ProjectAssignment';
import { ProjectResult } from '../../../modules/common/types/types';
import { ProjectsService } from '../../../modules/common/services';
import { GlobalAdministrationService, Group } from '../service/GlobalAdministrationService';
import { message } from 'antd';
import { NodeDragEventParams } from 'rc-tree/lib/contextTypes';
import { EventDataNode } from 'antd/lib/tree';
import { UserModel } from '../types/UserModel';

type ProjectsDict = {
    [key: string]: string[];
};

export class ProjectAssignmentVisualStore {
    @observable
    isLoading: boolean = false;

    @observable
    users: UserModel[] = [];

    @observable
    groupUsersDict: ProjectsDict = {};

    groups: Group[];

    projects: ProjectResult[];

    @observable
    userProjectsDict: ProjectsDict = {};

    @observable
    groupsProjectsDict: ProjectsDict = {};

    @observable
    currentSelectedNode: string;

    @observable
    isAddGroupDialogVisible: boolean = false;

    @observable
    usernameFilter: string = '';

    @observable
    treeExpandedKeys: string[] = [];

    @observable
    selectedProjectUserIds: Set<string> = new Set();

    @observable
    originalProjectUserIds: Set<string> = new Set();

    @observable
    pendingRoleChanges: Map<string, string[]> = new Map();

    @observable
    assignedUsersFilter: string = '';

    @observable
    otherUsersFilter: string = '';

    @observable
    currentProjectId: string = '';

    @observable
    isSynchronizingAssignmentsWithKeycloak: boolean = false;

    projectAssignments: ProjectAssignment[] | null;

    groupToRename: string | null = null;

    optionsValue: string = 'All';

    constructor(
        private errorStore: ErrorStore,
        private service: GlobalAdministrationService,
        private projectService: ProjectsService
    ) {
        this.saveProjectAssignments = this.saveProjectAssignments.bind(this);
        this.setupUsernameFilterReaction();
    }

    setupUsernameFilterReaction() {
        reaction(
            () => this.applyUsernameFilter,
            () => {
                this.currentSelectedNode = '';

                if (this.applyUsernameFilter) {
                    const treeExpandedKeys = Object.keys(this.filteredGroupUsersDict);

                    if (this.ungroupedUsernames.length) {
                        treeExpandedKeys.push('ungGrouped');
                    }

                    this.setTreeExpandedKeys(treeExpandedKeys);
                } else {
                    this.setTreeExpandedKeys([]);
                }
            }
        );
    }

    @action.bound
    setIsAddGroupDialogVisible(isVisible: boolean) {
        this.isAddGroupDialogVisible = isVisible;
    }

    @action.bound
    setUsernameFilter(usernameFilter: string) {
        this.usernameFilter = usernameFilter;
    }

    @action.bound
    setTreeExpandedKeys(treeExpandedKeys: string[]) {
        this.treeExpandedKeys = treeExpandedKeys;
    }

    @action.bound
    setAssignedUsersFilter(filter: string) {
        this.assignedUsersFilter = filter;
    }

    @action.bound
    setOtherUsersFilter(filter: string) {
        this.otherUsersFilter = filter;
    }

    @action.bound
    setCurrentProjectId(projectId: string) {
        this.currentProjectId = projectId;
    }

    @action.bound
    clearUserFilters() {
        this.assignedUsersFilter = '';
        this.otherUsersFilter = '';
    }

    @action.bound
    setSelectedProjectUserIds(userIds: Set<string>) {
        this.selectedProjectUserIds = userIds;
    }

    @action.bound
    setPendingRoleChanges(pendingRoleChanges: Map<string, string[]>) {
        this.pendingRoleChanges = pendingRoleChanges;
    }

    @action.bound
    clearPendingChanges() {
        this.selectedProjectUserIds = new Set();
        this.originalProjectUserIds = new Set();
        this.pendingRoleChanges = new Map();
        this.currentProjectId = '';
        this.clearUserFilters();
    }

    @action.bound
    handleAssignUser(userId: string) {
        this.selectedProjectUserIds = new Set(this.selectedProjectUserIds.add(userId));
    }

    @action.bound
    handleUnassignUser(userId: string) {
        const newSet = new Set(this.selectedProjectUserIds);
        newSet.delete(userId);
        this.selectedProjectUserIds = newSet;
    }

    @action.bound
    handleToggleUserAssignment(userId: string, isAssigned: boolean) {
        if (isAssigned) {
            this.handleAssignUser(userId);
        } else {
            this.handleUnassignUser(userId);
        }
    }

    @action.bound
    handleUserRolesChange(userId: string, roles: string[]) {
        this.pendingRoleChanges = new Map(this.pendingRoleChanges.set(userId, roles));
    }

    getUserProjectRoles(userId: string, projectId: string): string[] {
        if (this.pendingRoleChanges.has(userId)) {
            return this.pendingRoleChanges.get(userId)!;
        }

        const user = this.users?.find(u => u.id === userId);
        return user?.objectRoleAssignments?.[projectId] || [];
    }

    getUserGlobalRoles(userId: string): string[] {
        const user = this.users?.find(u => u.id === userId);
        return user?.roles?.map(role => role.name) || [];
    }

    getAllUserRoles(userId: string, projectId: string): { projectRoles: string[]; globalRoles: string[] } {
        const projectRoles = this.getUserProjectRoles(userId, projectId);
        const globalRoles = this.getUserGlobalRoles(userId);

        return { projectRoles, globalRoles };
    }

    isGroupAssignment(userId: string, projectId: string): boolean {
        const assignedThroughGroup = Object.keys(this.groupsProjectsDict || {}).find(
            groupId =>
                this.groupsProjectsDict[groupId]?.includes(projectId) && this.groupUsersDict[groupId]?.includes(userId)
        );

        return !!assignedThroughGroup;
    }

    @action
    async createUpdateGroup(name: string) {
        this.isLoading = true;
        const resp = await this.service.createUpdateGroup(name, this.groupToRename);
        if (resp.status === 200) {
            const isActionSuccessfull = resp.data;
            if (!isActionSuccessfull) {
                message.error('There were problems creating a group user, please report to administrator');
                return;
            }
            await this.getGroups();
            if (this.groupToRename) {
                message.success('Group has been updated');
            } else {
                message.success('New group has been created');
            }
        }
        this.groupToRename = null;
        this.setIsAddGroupDialogVisible(false);
        this.isLoading = false;
    }

    async handleRenameGroupClick(id: string) {
        this.groupToRename = id;
        this.setIsAddGroupDialogVisible(true);
    }

    async deleteGroup(id: string) {
        const resp = await this.service.deleteGroup(id);
        const isActionSuccessfull = resp;
        if (!isActionSuccessfull) {
            message.error('There were problems deleting a group, please report to administrator');
            return;
        }

        delete this.groupUsersDict[id];
        await this.getGroups();
        message.success('Group has been successfully deleted');
    }

    @action.bound
    async handleNodeDrop(data: NodeDragEventParams & { dragNode: EventDataNode }) {
        const draggedUser = this.users.find(x => x.id === data.dragNode.key);
        const dropGroup = this.groups.find(x => x.usersGroup.id === data.node.key);
        if ((dropGroup || data.node.key === 'ungGrouped') && draggedUser) {
            let resp = null;
            const groupId = Object.keys(this.groupUsersDict).find(key =>
                this.groupUsersDict[key].find(u => u === draggedUser.id)
            );
            if (groupId === (dropGroup && dropGroup!.usersGroup.id)) {
                return Promise.resolve();
            }

            const missingGroupAssignmentsIds = [groupId, dropGroup?.usersGroup.id].filter(
                (id): id is string => !!id && !this.projectAssignments?.some(p => p.userId === id)
            );

            if (missingGroupAssignmentsIds.length > 0) {
                await this.createMissingGroupAssignments(missingGroupAssignmentsIds);
            }

            resp = await this.service.changeUserGroup(draggedUser.id!, groupId, dropGroup && dropGroup.usersGroup);
            if (resp.status === 200) {
                const isActionSuccessfull = resp.data;
                if (!isActionSuccessfull) {
                    message.error('There were problems updating user group, please report to administrator');
                    return;
                }

                await this.getGroups();
            }
        }
    }

    @computed
    get hasPendingChanges(): boolean {
        const userChanges =
            this.selectedProjectUserIds.size !== this.originalProjectUserIds.size ||
            !Array.from(this.selectedProjectUserIds).every(id => this.originalProjectUserIds.has(id));

        return userChanges || this.pendingRoleChanges.size > 0;
    }

    @computed
    get selectedKeys() {
        return (
            (this.userProjectsDict[this.currentSelectedNode] && this.userGroupKeys) ||
            this.groupsProjectsDict[this.currentSelectedNode]
        );
    }

    get userGroupKeys() {
        const groupIds = Object.keys(this.groupUsersDict).filter(key =>
            this.groupUsersDict[key].find(u => u === this.currentSelectedNode)
        );
        return ((groupIds.length && _.flatten(groupIds.map(g => this.groupsProjectsDict[g]))) || []).concat(
            this.userProjectsDict[this.currentSelectedNode]
        );
    }

    @computed
    get selectedNodeIsUser() {
        return !!this.currentSelectedNode && !this.groups.find(g => g.usersGroup.id === this.currentSelectedNode);
    }

    @computed
    get applyUsernameFilter() {
        return this.usernameFilter.trim().length >= 2;
    }

    @computed
    get filteredUsers() {
        if (!this.applyUsernameFilter) {
            return this.users;
        }

        const searchTerm = this.usernameFilter.toLocaleLowerCase();

        return this.users.filter(
            u =>
                u.userName.toLocaleLowerCase().includes(searchTerm) ||
                u.firstName?.toLocaleLowerCase().includes(searchTerm) ||
                u.lastName?.toLocaleLowerCase().includes(searchTerm) ||
                this.emailUsernameMatchesSearchTerm(u, searchTerm)
        );
    }

    private emailUsernameMatchesSearchTerm(u: UserModel, searchTerm: string): boolean {
        if (u.email && typeof u.email === 'string') {
            const emailParts = u.email.split('@');
            if (emailParts.length === 2) {
                return emailParts[0].toLocaleLowerCase().includes(searchTerm);
            }
        }
        return false;
    }
    @computed
    get filteredUngroupedUsers() {
        return _.difference(
            this.filteredUsers.map(u => u.id),
            _.flatten(Object.values(this.filteredGroupUsersDict))
        );
    }

    @computed
    get ungroupedUsernames() {
        return this.filteredUngroupedUsers.map(x => this.getUserName(x)).sort();
    }

    @computed
    get filteredGroupUsersDict() {
        if (!this.applyUsernameFilter) {
            return this.groupUsersDict;
        }

        return _.pickBy(this.groupUsersDict, ids => ids.some(id => this.filteredUsers.some(u => u.id === id)));
    }

    @computed
    get selectedUser() {
        return this.users.find(u => u.id === this.currentSelectedNode);
    }

    @computed
    get selectedGroup() {
        return this.groups.find(g => g.usersGroup.id === this.currentSelectedNode);
    }

    @computed
    get projectUserCounts() {
        if (!this.projects || !this.projectAssignments) {
            return [];
        }

        return this.projects.map(project => {
            const directUsers = this.userProjectsDict
                ? Object.keys(this.userProjectsDict).filter(userId =>
                      this.userProjectsDict[userId].includes(project.id)
                  ).length
                : 0;

            const groupUsers = this.groupsProjectsDict
                ? Object.keys(this.groupsProjectsDict)
                      .filter(groupId => this.groupsProjectsDict[groupId].includes(project.id))
                      .reduce((count, groupId) => {
                          return count + (this.groupUsersDict[groupId]?.length || 0);
                      }, 0)
                : 0;

            return {
                projectId: project.id,
                projectName: project.name,
                assignedUsersCount: directUsers + groupUsers
            };
        });
    }

    @computed
    get currentProjectAssignedUsers() {
        if (!this.users || !this.currentProjectId) {
            return [];
        }

        return this.users.filter(user => this.selectedProjectUserIds.has(user.id));
    }

    @computed
    get currentProjectOtherUsers() {
        if (!this.users) {
            return [];
        }

        return this.users;
    }

    @computed
    get filteredCurrentProjectAssignedUsers() {
        return this.filterUsers(this.currentProjectAssignedUsers, this.assignedUsersFilter);
    }

    @computed
    get filteredCurrentProjectOtherUsers() {
        return this.filterUsers(this.currentProjectOtherUsers, this.otherUsersFilter);
    }

    @computed
    get currentProjectAssignedUsersOptions() {
        return this.getUserAutocompleteOptions(this.currentProjectAssignedUsers);
    }

    @computed
    get currentProjectOtherUsersOptions() {
        return this.getUserAutocompleteOptions(this.currentProjectOtherUsers);
    }

    filterUsers(users: UserModel[], filterTerm: string): UserModel[] {
        if (!filterTerm) {
            return users;
        }
        const searchTerm = filterTerm.toLowerCase();
        return users.filter(
            user =>
                user.userName.toLowerCase().includes(searchTerm) ||
                (user.email || '').toLowerCase().includes(searchTerm)
        );
    }

    getUserAutocompleteOptions(users: UserModel[]): string[] {
        return users.map(u => u.userName);
    }

    @action.bound
    initializeSelectedProjectUserIds(projectId: string) {
        this.setCurrentProjectId(projectId);
        if (!this.users || !projectId) {
            this.selectedProjectUserIds = new Set();
            this.originalProjectUserIds = new Set();
            return;
        }

        const userSet = new Set<string>();

        Object.keys(this.userProjectsDict).forEach(userId => {
            if (this.userProjectsDict[userId].includes(projectId)) {
                userSet.add(userId);
            }
        });

        Object.keys(this.groupsProjectsDict).forEach(groupId => {
            if (this.groupsProjectsDict[groupId].includes(projectId)) {
                const groupUsers = this.groupUsersDict[groupId] || [];
                groupUsers.forEach(userId => userSet.add(userId));
            }
        });

        this.selectedProjectUserIds = new Set(userSet);
        this.originalProjectUserIds = new Set(userSet);
    }

    getProjectUserAssignments(projectId: string) {
        if (!this.users || !projectId) {
            return { assignedUsers: [], otherUsers: [], assignedUserSet: new Set<string>() };
        }

        const assigned = this.users.filter(user => this.selectedProjectUserIds.has(user.id));
        const allUsers = this.users;

        return { assignedUsers: assigned, otherUsers: allUsers, assignedUserSet: this.selectedProjectUserIds };
    }

    @action.bound
    async handleNodeSelection(selectedKey: string | null) {
        if (!selectedKey) {
            return;
        }
        this.currentSelectedNode = selectedKey;
    }

    isProjectCheckboxDisabled(rowId: string) {
        if (this.users.map(x => x.id).includes(this.currentSelectedNode)) {
            const groupId = this.getGroupByUser(this.currentSelectedNode);
            if (groupId) {
                if (this.groupsProjectsDict[groupId].includes(rowId)) {
                    return true;
                }
            }
        }
        return false;
    }

    getUserName(id: string) {
        return this.users.find(g => g.id === id)?.userName ?? id;
    }

    getUserIdByUsername(userName: string) {
        return this.users.find(g => g.userName === userName)?.id ?? userName;
    }

    @action
    setSelectedKey(selectedProject: string, isSelected: boolean) {
        const group = this.groups.find(g => g.usersGroup.id === this.currentSelectedNode);
        if (group) {
            if (isSelected) {
                this.groupsProjectsDict[this.currentSelectedNode] = [
                    ...this.groupsProjectsDict[this.currentSelectedNode],
                    selectedProject
                ];
            } else {
                const index = this.groupsProjectsDict[this.currentSelectedNode].indexOf(selectedProject);
                const projects = this.groupsProjectsDict[this.currentSelectedNode].slice();
                projects.splice(index, 1);
                this.groupsProjectsDict[this.currentSelectedNode] = projects;
            }
        } else {
            if (isSelected) {
                this.userProjectsDict[this.currentSelectedNode].push(selectedProject);
            } else {
                const index = this.userProjectsDict[this.currentSelectedNode].indexOf(selectedProject);
                const projects = this.userProjectsDict[this.currentSelectedNode].slice();
                projects.splice(index, 1);
                this.userProjectsDict[this.currentSelectedNode] = projects;
            }
        }
    }

    @action
    setAllKeys(isChecked: boolean) {
        const group = this.groups.find(g => g.usersGroup.id === this.currentSelectedNode);
        if (group) {
            if (isChecked) {
                this.groupsProjectsDict[this.currentSelectedNode] = this.projects.map(p => p.id);
            } else {
                this.groupsProjectsDict[this.currentSelectedNode] = [];
            }
        } else {
            if (isChecked) {
                this.userProjectsDict[this.currentSelectedNode] = this.projects.map(p => p.id);
            } else {
                this.userProjectsDict[this.currentSelectedNode] = [];
            }
        }
    }

    @action
    async getUsersAndGroups() {
        this.isLoading = true;
        this.groupUsersDict = {};

        try {
            await this.getProjects();
            await this.getProjectAssignments();
            await this.getUsers().catch(err => {
                return Promise.reject(err);
            });
            await this.getGroups();
        } catch (error) {
            console.error('Error in getUsersAndGroups:', error);
            this.errorStore.addError(error);
        } finally {
            this.isLoading = false;
        }
    }

    async getProjectAssignments() {
        this.projectAssignments = await this.service.getProjectAssignments();
    }

    @action.bound
    async getUsers() {
        const users = (await this.service.getAppUsers(true)) as UserModel[] | { error: string };
        if ((users as { error: string }).error) {
            this.isLoading = false;
            return Promise.reject('No perimission for current resource');
        } else {
            this.users = users as UserModel[];
            for (const user of this.users) {
                this.userProjectsDict[user.id] = this.getProjectsById(user.id);
            }
            return Promise.resolve();
        }
    }

    @action
    async getGroups() {
        try {
            this.groups = (await this.service.getGroups()) as Group[];
            this.groups.forEach(async g => {
                const users = g.users;
                const groupId = g.usersGroup.id;
                this.groupUsersDict[groupId] = users.sort((a, b) => (a.userName > b.userName ? 1 : -1)).map(u => u.id);
                const userGroupProjects = this.getProjectsById(groupId);
                this.groupsProjectsDict[groupId] = userGroupProjects;
            });
        } catch (err) {
            this.errorStore.addError(err);
        }
    }

    get unGroupedUsers() {
        return _.difference(this.users && this.users.map(u => u.id), _.flatten(Object.values(this.groupUsersDict)));
    }

    async getProjects() {
        const projects = await this.projectService.getProjects();
        this.projects = projects;
    }

    @action.bound
    async saveGroupProjectAssignments() {
        if (!this.selectedGroup) {
            return;
        }

        const groupId = this.selectedGroup.usersGroup.id;
        const groupName = this.selectedGroup.usersGroup.name;
        const projects = this.groupsProjectsDict[groupId];
        const usersWithinGroup = this.groupUsersDict[groupId];
        const users = usersWithinGroup.filter(this.filterValidUsers).map(u => {
            return {
                userId: u,
                userName: this.users.find(user => user.id === u)!.userName
            };
        });

        await this.service.createUpdateGroupProjectAssignments({
            groupId,
            projects,
            users,
            groupName
        });
    }

    @action.bound
    async saveUserProjectAssignments() {
        if (!this.selectedUser) {
            return;
        }

        const userId = this.selectedUser.id;
        const userName = this.selectedUser.userName;
        const projects = this.userProjectsDict[userId];

        await this.service.createUpdateUserProjectAssignments({
            userId,
            userName,
            projects
        });
    }

    @action
    async saveProjectAssignments() {
        if (this.selectedUser) {
            await this.saveUserProjectAssignments();
        } else {
            await this.saveGroupProjectAssignments();
        }

        await this.getProjectAssignments();
    }

    @action.bound
    async saveProjectUserAssignments(projectId: string) {
        if (this.selectedProjectUserIds.size > 0) {
            await this.service.createUpdateProjectUserAssignments({
                projectId,
                userIds: Array.from(this.selectedProjectUserIds)
            });
        }

        if (this.pendingRoleChanges.size > 0) {
            const roleChangePromises = Array.from(this.pendingRoleChanges.entries()).map(([userId, roles]) =>
                this.service.changeObjectRoleAssignments(userId, projectId, 'project', roles)
            );
            await Promise.all(roleChangePromises);
        }

        this.clearPendingChanges();

        if (this.getUsersAndGroups) {
            await this.getUsersAndGroups();
        }
    }

    @action
    getObjectRoleAssignments(userId: string, objectId: string) {
        const user = this.users.find(u => u.id === userId);

        if (!user || !user.objectRoleAssignments) {
            return null;
        }

        return user.objectRoleAssignments[objectId];
    }

    @action.bound
    private filterValidUsers(userId: string) {
        if (!this.users) {
            return false;
        }

        if (this.users.find(user => user.id === userId)) {
            return true;
        }

        console.warn(`User '${userId}' not found in users list`);
        return false;
    }

    @action.bound
    async synchronizeAssignmentsWithKeycloak() {
        try {
            this.isSynchronizingAssignmentsWithKeycloak = true;
            const resp = await this.service.synchronizeAssignmentsWithKeycloak();
            if (resp.isOk()) {
                message.success('Assignments synchronized with Keycloak');
            } else {
                message.error('Failed to synchronize assignments with Keycloak');
            }
        } catch (error) {
            this.errorStore.addError(error);
            message.error('Failed to synchronize assignments with Keycloak');
        } finally {
            this.isSynchronizingAssignmentsWithKeycloak = false;
        }
    }

    private getProjectsById(id: string) {
        if (this.projectAssignments) {
            const item = this.projectAssignments.find(p => p.userId === id);
            return (item && item.projects) || [];
        } else {
            return [];
        }
    }

    private getGroupByUser(userId: string) {
        return Object.keys(this.groupUsersDict).find(key => this.groupUsersDict[key].find(u => u === userId));
    }

    private async createMissingGroupAssignments(missingIds: string[]) {
        const promises = missingIds.map(missingId => {
            const missingName = this.groups.find(g => g.usersGroup.id === missingId)?.usersGroup.name;
            return this.service.createUpdateGroupProjectAssignments({
                groupId: missingId,
                groupName: missingName ?? '',
                projects: [],
                users: []
            });
        });

        await Promise.all(promises);
    }
}

export default ProjectAssignmentVisualStore;
